import { useEffect, useState } from 'react'
import { Formik } from 'formik'
import dayjs, { Dayjs } from 'dayjs'

import { multiStep } from 'components/Wizards/MultiStepWizard/multiStep'
import { scheduleContent } from 'components/Wizards/utils/postScheduling'
import { useHistory } from 'react-router-dom'
import { useCreateCalendarItem } from 'hooks/queries/calendar/useCreateCalendarItem'
import { useModals } from 'almanac-ui/Modal'

import { DayAndTime } from 'components/Wizards/ContentPlanning/ScheduleContent/components/StartDateAndPostFrequency/StartDateAndPostFrequencyModal'
import {
  PostingCadence,
  ScheduleContent,
  ScheduledContent,
} from 'components/Wizards/ContentPlanning/ScheduleContent/ScheduleContent'
import { StepSchema } from 'components/Wizards/schemas/Types'
import { StepProps } from 'components/Wizards/MultiStepWizard/Types'
import { AlmanacFile } from 'types'
import { Header as ContentPlanningHeader } from 'components/Wizards/ContentPlanning/Header/Header'
import { Design } from '../../RecommendMoreContentWizard'

type Props = StepProps & {
  designs: (Design | undefined)[]
  integrationName: string
  project: AlmanacFile
  deleteDesign: (index: number) => void
}

export const Plan = multiStep(
  ({ schema, onBack, onSubmit, initialValues, designs, integrationName, project, deleteDesign }: Props) => {
    const { meta } = schema.describe()
    const initialStartDate = dayjs().minute(0).second(0).add(1, 'day')
    const [scheduledContent, setScheduledContent] = useState<ScheduledContent[]>([])
    const createCalendarItemMutation = useCreateCalendarItem(project.id)
    const history = useHistory()
    const { closeModal } = useModals()

    useEffect(() => {
      const filteredDesigns = designs.filter((d) => !!d) as Design[]
      if (scheduledContent.length > 0) return

      const initialScheduledContent = filteredDesigns.map((design, index) => {
        return {
          uuid: design.id,
          text: design.captions?.[0] || '',
          postAt: initialStartDate.add(index, 'weeks'),
          previewUrl: design.preview_url,
        }
      })
      setScheduledContent(initialScheduledContent)
    }, [designs, initialStartDate, setScheduledContent, scheduledContent])

    const handleScheduleChange = (schedule: { daysAndTimes: DayAndTime[]; startDate: Dayjs }) => {
      const { daysAndTimes, startDate } = schedule
      const content = scheduleContent(scheduledContent, daysAndTimes, startDate)

      setScheduledContent(content)
    }

    const saveSchedule = async () => {
      scheduledContent.forEach((content, index) => {
        const design = designs.find((d) => d?.id === content.uuid)

        if (!design) return

        createCalendarItemMutation.mutate({
          unscheduledItem: {
            key: content.uuid,
            title: design.title,
            post_at: content.postAt.toISOString(),
            image_url: content.previewUrl || '',
            postable: true,
            is_generating: false,
            created_at: design.created_at,
            updated_at: design.created_at,
            is_carousel: false,
          },
          id: content.uuid,
          status: 'Autoscheduled',
          postAt: content.postAt.toISOString(),
        })
      })

      if (project.url) history.push(project.url)
      closeModal()
    }

    return (
      <Formik
        enableReinitialize
        validationSchema={schema}
        onSubmit={onSubmit}
        initialValues={schema.cast(initialValues, { assert: false })}
      >
        {({ submitForm }) => (
          <ScheduleContent
            header={
              <ContentPlanningHeader
                header={`Plan Your ${integrationName} Calendar`}
                subHeader="Select a date and time for each of your posts or have Blaze pick for you."
              />
            }
            inputPlaceholder="Write your own caption..."
            scheduledContent={scheduledContent}
            setScheduledContent={(newContent) => {
              const newContentIds = (newContent as ScheduledContent[]).map((nC) => nC.uuid)

              scheduledContent.forEach((scheduledDesign, index) => {
                if (newContentIds.includes(scheduledDesign.uuid)) return

                deleteDesign(index)
              })

              setScheduledContent(newContent)
            }}
            onSubmit={async () => {
              await saveSchedule()
              submitForm()
            }}
            goBack={() => onBack?.()}
            initialPostingCadence={PostingCadence.Once}
            onScheduleChange={handleScheduleChange}
            previousLabel={meta.previousLabel}
            submitLabel={meta.submitLabel}
            lineClamp={3}
            initialStartDate={initialStartDate}
            schema={schema as StepSchema}
            campaignName="noop"
          />
        )}
      </Formik>
    )
  },
)
